/**
 * "Ask Flight Cost Estimates" bar for the list page: pick the event, open the
 * agent chat (one chat per event, like the R&P and manifest agents).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { AssessmentsService, type EventOption } from '@/services/AssessmentsService';
import { Bot } from 'lucide-react';

const svc = new AssessmentsService();

export function FlightCostAgentLauncher({ onOpen, defaultEventId }: { onOpen: (eventId: string) => void; defaultEventId?: string | null }) {
	const [events, setEvents] = useState<EventOption[] | null>(null);
	const [eventId, setEventId] = useState<string>(defaultEventId ?? '');

	useEffect(() => {
		svc.getEventOptions({ includeEmpty: true }).then(setEvents).catch(() => setEvents([]));
	}, []);

	const sorted = useMemo(
		() => (events ?? []).slice().sort((a, b) => `${a.client_name} ${a.name}`.localeCompare(`${b.client_name} ${b.name}`)),
		[events],
	);

	return (
		<div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
			<Bot className="w-5 h-5 text-secondary-600 shrink-0" />
			<div className="text-sm text-gray-700 mr-auto">
				<span className="font-medium text-gray-900">Flight Cost Estimates</span> sets up and prices estimates with you, in plain language.
			</div>
			<select
				value={eventId}
				onChange={(e) => setEventId(e.target.value)}
				disabled={!events}
				aria-label="Event"
				className="h-8 max-w-xs text-xs rounded-md border border-gray-200 bg-white px-2 focus:outline-none focus:ring-1 focus:ring-gray-300"
			>
				<option value="">{events ? 'Choose an event…' : 'Loading events…'}</option>
				{sorted.map((ev) => (
					<option key={ev.id} value={ev.id}>{ev.client_name ? `${ev.client_name} — ${ev.name}` : ev.name}</option>
				))}
			</select>
			<Button variant="secondary" size="sm" disabled={!eventId} onClick={() => eventId && onOpen(eventId)} leftIcon={<Bot className="w-4 h-4" />}>
				Ask Flight Cost Estimates
			</Button>
		</div>
	);
}
